import { create } from "zustand";
import type { Service } from "@/lib/types";

// Columnar batch as received via the dyno_samples WS message. Each array is
// the same length (one entry per sample timestamp). Field set matches the
// edge publisher in app.py (_DYNO_FAST_FIELDS) plus the canonical "ts" array.
export interface DynoSampleBatch {
  ts: number[];                              // sample timestamps, ms epoch
  voltage_l1?: (number | null)[];
  voltage_l2?: (number | null)[];
  voltage_l3?: (number | null)[];
  current_l1?: (number | null)[];
  current_l2?: (number | null)[];
  current_l3?: (number | null)[];
  current_n?: (number | null)[];
  active_power_l1?: (number | null)[];
  active_power_l2?: (number | null)[];
  active_power_l3?: (number | null)[];
  active_power?: (number | null)[];
  apparent_power_l1?: (number | null)[];
  apparent_power_l2?: (number | null)[];
  apparent_power_l3?: (number | null)[];
  apparent_power?: (number | null)[];
  power_factor_l1?: (number | null)[];
  power_factor_l2?: (number | null)[];
  power_factor_l3?: (number | null)[];
  power_factor?: (number | null)[];
  frequency?: (number | null)[];
}

interface LiveFeedState {
  services: Record<string, Service>;
  lastSnapshotAt: number | null;
  connected: boolean;
  // Latest dyno_samples batch per system_id. Replaced on every WS message
  // arrival. Components watching dynoTick read this imperatively to drain
  // new samples into their own ring buffers — keeping the heavy data
  // outside Zustand's diff/render path. See kieran-ts review note.
  latestDynoBatch: Record<string, DynoSampleBatch>;
  // Monotonic counter — increments on every dyno_samples arrival.
  // Subscribers use this as their "new data" trigger.
  dynoTick: number;
  // Stable-merge reducer — reuse prev reference when no scalar changed so
  // Zustand per-service selectors skip re-render. See Dyno plan appendix §B.
  applySnapshot: (incoming: Service[]) => void;
  // Apply a dyno_samples batch: update each system's latest scalars (so the
  // tile's % capacity / P / PF / Hz / VA all reflect the freshest sample)
  // and publish the columnar batch for the sparkline to drain.
  applyDynoSamples: (systems: DynoSamplesIncoming[]) => void;
  // A2.2b composer — patch PQ-tuning fields (inominal status, flagged,
  // severity, etc.) from the retained ovr/dyno/tuning/<sid> topic.
  // No effect on V/I/P scalars; preserves them.
  applyDynoTuning: (payload: DynoTuningIncoming) => void;
  // A2.2b composer — patch demand fields from ovr/dyno/demand/<sid>.
  applyDynoDemand: (payload: DynoDemandIncoming) => void;
  // Live PQ scalars (THD / TDD / K-factor per phase) from ovr/dyno/pq/<sid>.
  applyDynoPq: (payload: DynoPqIncoming) => void;
  // C1 + C2 — patch identity / ownership fields from
  // ovr/registry/system/<sid>. Closes the silent gap where MQTT-path
  // tiles had no meter_model (label) or live node_id (drift indicator).
  applySystemRegistry: (payload: SystemRegistryIncoming) => void;
  // C3 — patch sig (ok/stale) from ovr/dyno/sig/<sid>. Without this,
  // offline meters under ?mqtt_v2=1 silently look healthy because the
  // Service default was "ok".
  applyDynoSig: (payload: DynoSigIncoming) => void;
  setConnected: (ok: boolean) => void;
  upsertService: (svc: Service) => void;
}

// Payload of retained `ovr/dyno/tuning/<sid>` — see pq_tuning_cache.py.
export interface DynoTuningIncoming {
  system_id: string;
  inominal_amps?: number | null;
  inominal_status?: Service["inominal_status"];
  data_presence_state?: Service["data_presence_state"];
  recommended_inominal_amps?: number | null;
  flagged?: boolean | null;
  reason?: Service["reason"];
  dominant_severity?: Service["dominant_severity"];
  events_last_hour?: number | null;
  // Tier-history fields not currently rendered by Service — accepted but unused.
  p95_current_30m?: number | null;
  peak_current_15m?: number | null;
  avg_current_15m?: number | null;
}

// Payload of retained `ovr/dyno/demand/<sid>` — see pq_tuning_cache.py.
export interface DynoDemandIncoming {
  system_id: string;
  demand_w?: number | null;
  demand_source?: Service["demand_source"];
}

// Payload of retained `ovr/dyno/pq/<sid>` — see pq_tuning_cache._pq_payload.
// Per-phase arrays [A, B, C]; values are % (THD/TDD) or unitless (K-factor).
export interface DynoPqIncoming {
  system_id: string;
  v_thd?: (number | null)[];
  i_thd?: (number | null)[];
  i_tdd?: (number | null)[];
  i_kfactor?: (number | null)[];
}

// Payload of retained `ovr/dyno/sig/<sid>` — see cloud_dyno_sig_publisher.py.
// Closes the C3 silent gap where MQTT-path tiles defaulted to sig="ok"
// forever (offline meters looked healthy). 10s cadence cloud-side; only
// state changes publish thanks to content-hash dedup.
export interface DynoSigIncoming {
  system_id: string;
  sig?: Service["sig"];
  ts?: number;
}

// Payload of retained `ovr/registry/system/<sid>` — see mqtt_handlers.py
// `_upsert_system_registry`. Carries the slow-changing identity + ownership
// fields. The MQTT-path composer reads meter_model + node_id from here to
// close the C1 + C2 silent gaps (audit 2026-05-28).
export interface SystemRegistryIncoming {
  system_id: string;
  type?: string | null;
  source?: string | null;
  node_id?: string | null;
  portal_id?: string | null;
  meter_model?: string | null;
  event_id?: string | null;
  window_id?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  gps_source?: string | null;
  // C4 — list of fields derived (vs measured). Drives the `*` marker +
  // popover on the dyno tile. Typically ["s_total","pf"] for Acuvim II-R,
  // empty for Acuvim 3. Cloud relays from the edge realtime payload.
  computed_fields?: string[] | null;
}

// Shape received from the WS handler. Each system has all _DYNO_FAST_FIELDS
// as same-length arrays (one entry per sample timestamp in ts).
export interface DynoSamplesIncoming {
  system_id: string;
  type?: string;
  ts: number[];
  [field: string]: number[] | string | undefined;
}

// Helper — latest non-null value at the end of an array, falling back to
// undefined when the array is empty / missing / entirely null. Used to lift
// a column's last sample into the scalar slot on Service for tile rendering.
function latestNonNull(arr: (number | null)[] | undefined): number | undefined {
  if (!arr || arr.length === 0) return undefined;
  for (let i = arr.length - 1; i >= 0; i--) {
    const v = arr[i];
    if (v != null) return v;
  }
  return undefined;
}

export const useLiveFeed = create<LiveFeedState>((set) => ({
  services: {},
  lastSnapshotAt: null,
  connected: false,
  latestDynoBatch: {},
  dynoTick: 0,

  applySnapshot: (incoming) =>
    set((state) => {
      const next: Record<string, Service> = {};
      let changed = false;
      for (const svc of incoming) {
        const prev = state.services[svc.system_id];
        // Include demand_w + demand_source + meter_model in the unchanged
        // check so a snapshot's first delivery of these fields lands in the
        // store even when scalars happen to match (e.g. when applyDynoSamples
        // populated the entry first at zero load without setting demand_w).
        const scalarsUnchanged =
          prev &&
          prev.v?.[0] === svc.v?.[0] &&
          prev.v?.[1] === svc.v?.[1] &&
          prev.v?.[2] === svc.v?.[2] &&
          prev.i?.[0] === svc.i?.[0] &&
          prev.i?.[1] === svc.i?.[1] &&
          prev.i?.[2] === svc.i?.[2] &&
          prev.i_n === svc.i_n &&
          prev.p === svc.p &&
          prev.pf === svc.pf &&
          prev.f === svc.f &&
          prev.demand_w === svc.demand_w &&
          prev.demand_source === svc.demand_source &&
          prev.meter_model === svc.meter_model &&
          // PQ-tuning fields — inominal_status is load-bearing for the
          // dialog's syncing→ok transition; the rest drive the badge.
          prev.inominal_status === svc.inominal_status &&
          prev.inominal_amps === svc.inominal_amps &&
          prev.flagged === svc.flagged &&
          prev.reason === svc.reason &&
          prev.dominant_severity === svc.dominant_severity &&
          prev.events_last_hour === svc.events_last_hour &&
          // U6 — data-presence detector state. MUST be in the scalarsUnchanged
          // check (per existing CLAUDE.md gotcha) so a transition
          // ok → silent → ok actually re-renders the tile rather than reusing
          // the prior service object reference.
          prev.data_presence_state === svc.data_presence_state;
        // sig drifts independently of scalars (e.g., an idle meter has
        // identical V/I/P every tick but the server still updates ts so the
        // 'stale' badge correctly flips back to 'ok' on each fresh tick).
        // Compare sig separately so we don't freeze the staleness signal
        // when scalars are unchanged.
        const sigUnchanged = prev?.sig === svc.sig;
        if (scalarsUnchanged && sigUnchanged) {
          // ts always updates from the latest snapshot so latency cells can
          // show fresh "Xs ago" even when V/I/P haven't moved. Per-field
          // ServiceTile selectors don't subscribe to ts and won't re-render.
          if (prev?.ts === svc.ts) {
            next[svc.system_id] = prev as Service;
          } else {
            next[svc.system_id] = { ...(prev as Service), ts: svc.ts };
            changed = true;
          }
        } else if (scalarsUnchanged && !sigUnchanged) {
          // Reuse prev for scalar-keyed selectors but refresh sig + ts.
          next[svc.system_id] = { ...(prev as Service), sig: svc.sig, ts: svc.ts };
          changed = true;
        } else {
          next[svc.system_id] = { ...prev, ...svc };
          changed = true;
        }
      }
      if (!changed && Object.keys(next).length === Object.keys(state.services).length) {
        return state;
      }
      return { services: next, lastSnapshotAt: Date.now() };
    }),

  applyDynoSamples: (systems) =>
    set((state) => {
      const nextBatches: Record<string, DynoSampleBatch> = { ...state.latestDynoBatch };
      const nextServices: Record<string, Service> = { ...state.services };
      for (const s of systems) {
        const sid = s.system_id;
        if (!sid || !Array.isArray(s.ts) || s.ts.length === 0) continue;
        // Stash the full columnar batch for downstream drain by the sparkline.
        const batch: DynoSampleBatch = { ts: s.ts as number[] };
        for (const [k, v] of Object.entries(s)) {
          if (k === "system_id" || k === "type" || k === "ts") continue;
          if (Array.isArray(v)) {
            (batch as unknown as Record<string, unknown>)[k] = v;
          }
        }
        nextBatches[sid] = batch;
        // Lift the most-recent non-null value of each per-phase / total field
        // into the Service scalar slot, so the tile's primitive selectors
        // (peak-leg amps for % capacity, p for DEMAND/INSTANT, pf, f, VA)
        // see fresh data without us having to re-think the existing reads.
        const prev = nextServices[sid] ?? {
          membership_id: "",
          window_id: "",
          system_id: sid,
          service_name: null,
          service_capacity_amps: null,
          system_type: "acuvim",
          node_id: null,
          joined_at: null,
          left_at: null,
          location: null,
        };
        const va = latestNonNull(batch.voltage_l1);
        const vb = latestNonNull(batch.voltage_l2);
        const vc = latestNonNull(batch.voltage_l3);
        const ia = latestNonNull(batch.current_l1);
        const ib = latestNonNull(batch.current_l2);
        const ic = latestNonNull(batch.current_l3);
        const p = latestNonNull(batch.active_power);
        const sTotal = latestNonNull(batch.apparent_power);
        const pfTotal = latestNonNull(batch.power_factor);
        const f = latestNonNull(batch.frequency);
        const i_n = latestNonNull(batch.current_n);
        // dyno_samples carries a per-system ts ARRAY (one ts per sample) at
        // the fast cadence — the freshest signal we have. Lift the latest
        // entry into Service.ts so latency cells reflect "time since last
        // sample" rather than the slower snapshot envelope ts.
        const sampleTs = batch.ts as number[] | undefined;
        const latestTs = sampleTs && sampleTs.length > 0
          ? sampleTs[sampleTs.length - 1]
          : prev.ts;
        nextServices[sid] = {
          ...prev,
          v: va != null && vb != null && vc != null ? [va, vb, vc] : prev.v,
          i: ia != null && ib != null && ic != null ? [ia, ib, ic] : prev.i,
          p: p ?? prev.p,
          s: sTotal ?? prev.s,
          pf: pfTotal ?? prev.pf,
          f: f ?? prev.f,
          i_n: i_n ?? prev.i_n,
          ts: latestTs ?? prev.ts,
          sig: "ok",  // fresh data means not stale
        };
      }
      return {
        latestDynoBatch: nextBatches,
        services: nextServices,
        dynoTick: state.dynoTick + 1,
        lastSnapshotAt: Date.now(),
      };
    }),

  applyDynoTuning: (payload) =>
    set((state) => {
      const sid = payload.system_id;
      if (!sid) return state;
      const prev = state.services[sid] ?? {
        membership_id: "",
        window_id: "",
        system_id: sid,
        service_name: null,
        service_capacity_amps: null,
        system_type: "acuvim",
        node_id: null,
        joined_at: null,
        left_at: null,
        location: null,
      };
      // Patch only tuning fields. Use `in` checks so an explicit null in the
      // payload clears the field (e.g. when a system loses its tuning ctx).
      const next: Service = { ...prev };
      if ("inominal_amps" in payload) next.inominal_amps = payload.inominal_amps ?? null;
      if ("inominal_status" in payload) next.inominal_status = payload.inominal_status ?? null;
      if ("data_presence_state" in payload) next.data_presence_state = payload.data_presence_state ?? null;
      if ("flagged" in payload) next.flagged = payload.flagged ?? null;
      if ("reason" in payload) next.reason = payload.reason ?? null;
      if ("dominant_severity" in payload) next.dominant_severity = payload.dominant_severity ?? null;
      if ("events_last_hour" in payload) next.events_last_hour = payload.events_last_hour ?? null;
      return { services: { ...state.services, [sid]: next } };
    }),

  applyDynoDemand: (payload) =>
    set((state) => {
      const sid = payload.system_id;
      if (!sid) return state;
      const prev = state.services[sid] ?? {
        membership_id: "",
        window_id: "",
        system_id: sid,
        service_name: null,
        service_capacity_amps: null,
        system_type: "acuvim",
        node_id: null,
        joined_at: null,
        left_at: null,
        location: null,
      };
      const next: Service = { ...prev };
      if ("demand_w" in payload) next.demand_w = payload.demand_w ?? null;
      if ("demand_source" in payload) next.demand_source = payload.demand_source ?? null;
      return { services: { ...state.services, [sid]: next } };
    }),

  applyDynoPq: (payload) =>
    set((state) => {
      const sid = payload.system_id;
      if (!sid) return state;
      const prev = state.services[sid] ?? {
        membership_id: "",
        window_id: "",
        system_id: sid,
        service_name: null,
        service_capacity_amps: null,
        system_type: "acuvim",
        node_id: null,
        joined_at: null,
        left_at: null,
        location: null,
      };
      const next: Service = { ...prev };
      if ("v_thd" in payload) next.v_thd = payload.v_thd ?? undefined;
      if ("i_thd" in payload) next.i_thd = payload.i_thd ?? undefined;
      if ("i_tdd" in payload) next.i_tdd = payload.i_tdd ?? undefined;
      if ("i_kfactor" in payload) next.i_kfactor = payload.i_kfactor ?? undefined;
      return { services: { ...state.services, [sid]: next } };
    }),

  applySystemRegistry: (payload) =>
    set((state) => {
      const sid = payload.system_id;
      if (!sid) return state;
      const prev = state.services[sid] ?? {
        membership_id: "",
        window_id: "",
        system_id: sid,
        service_name: null,
        service_capacity_amps: null,
        system_type: payload.type ?? "acuvim",
        node_id: null,
        joined_at: null,
        left_at: null,
        location: null,
      };
      const next: Service = { ...prev };
      // Patch identity fields. Use `in` checks so an explicit null clears
      // the field (matches the clear-on-deletion pattern that
      // emqx_publisher.publish_system_registry uses).
      if ("meter_model" in payload) next.meter_model = payload.meter_model ?? null;
      if ("node_id" in payload) next.node_id = payload.node_id ?? null;
      if ("location" in payload) next.location = payload.location ?? null;
      if ("computed_fields" in payload) {
        next.computed_fields = payload.computed_fields ?? [];
      }
      // system_type only updates when registry explicitly carries a type;
      // applyDynoSamples defaults to "acuvim" for newly-seen sids.
      if (payload.type) next.system_type = payload.type;
      return { services: { ...state.services, [sid]: next } };
    }),

  applyDynoSig: (payload) =>
    set((state) => {
      const sid = payload.system_id;
      if (!sid || !payload.sig) return state;
      const prev = state.services[sid];
      // Only act if there's an existing service entry — sig in isolation
      // for a system the tile hasn't otherwise heard of is useless. If
      // the entry doesn't exist yet, the retained replay on subscribe
      // means we'll just re-receive this payload after the first
      // applyDynoSamples / applySystemRegistry tick creates the entry.
      if (!prev) return state;
      if (prev.sig === payload.sig) return state;  // no-op
      return {
        services: { ...state.services, [sid]: { ...prev, sig: payload.sig } },
      };
    }),

  setConnected: (ok) => set({ connected: ok }),

  upsertService: (svc) =>
    set((state) => ({ services: { ...state.services, [svc.system_id]: svc } })),
}));
